import React from "react";
import { Modal } from "@mantine/core";
import AppointmentForm from "./AppointmentModal/AppointmentForm";
import MobileAppointmentForm from "./AppointmentModal/MobileAppointmentForm";
import { MobileTimeBlockSheet } from "../mobile/sheets/MobileTimeBlockSheet";
import { SchedulingActions } from "../hooks/useSchedulingActions";
import TimeBlockDrawer from "../timeblock/TimeBlockDrawer";
import type { DrawerSlotApi } from "../drawer/drawerSlot";
import type {
  CalendarEventType,
  TimeBlockEvent,
} from "../../../../types/calendar";

interface SchedulingModalsProps {
  scheduling: SchedulingActions;
  isMobile: boolean;
  /** Desktop time block drawer: slot (nested Repeat), conflicts, timezone. */
  drawerSlot?: Pick<DrawerSlotApi, "slot" | "openNested" | "closeNested">;
  events?: CalendarEventType[];
  timezone?: string;
  viewedDay?: Date;
  /** Desktop: a new time block's grid preview (TimeBlockDrawer onDraftChange). */
  onTimeBlockDraftChange?: (draft: TimeBlockEvent | null) => void;
}

/**
 * Legacy appointment modal (create only: mobile appointments; the
 * desktop booking drawer is booking/BookingPanel) and the time block surface
 * (desktop drawer / mobile bottom sheet, one form).
 */
const SchedulingModals: React.FC<SchedulingModalsProps> = ({
  scheduling,
  isMobile,
  drawerSlot,
  events = [],
  timezone,
  viewedDay = new Date(),
  onTimeBlockDraftChange,
}) => {
  const {
    selectedEvent,
    selectedSlot,
    timeBlockSeed,
    isLoading,
    isAppointmentModalOpen,
    isTimeBlockModalOpen,
    closeAppointmentModal,
    closeTimeBlockModal,
    handleAppointmentSubmit,
    handleTimeBlockSubmit,
    handleTimeBlockDelete,
  } = scheduling;

  return (
    <>
      {/* Appointment Modal */}
      <Modal
        opened={isAppointmentModalOpen}
        onClose={closeAppointmentModal}
        title="Add Appointment"
        size={isMobile ? undefined : "lg"}
        centered={!isMobile}
        fullScreen={isMobile}
      >
        {isMobile ? (
          <MobileAppointmentForm
            initialData={selectedSlot}
            onSubmit={handleAppointmentSubmit}
            isLoading={isLoading}
          />
        ) : (
          <AppointmentForm
            initialData={selectedSlot}
            onSubmit={handleAppointmentSubmit}
            isLoading={isLoading}
          />
        )}
      </Modal>

      {/* Time block: the drawer on desktop, the bottom sheet on mobile */}
      {isMobile ? (
        <MobileTimeBlockSheet
          opened={isTimeBlockModalOpen}
          onClose={closeTimeBlockModal}
          initialData={
            (selectedEvent as TimeBlockEvent | null) ?? timeBlockSeed ?? null
          }
          isEdit={!!selectedEvent}
          isLoading={isLoading}
          onSubmit={handleTimeBlockSubmit}
          onDelete={handleTimeBlockDelete}
          timezone={timezone}
          viewedDay={viewedDay}
          events={events}
        />
      ) : (
        <TimeBlockDrawer
          opened={isTimeBlockModalOpen}
          onClose={closeTimeBlockModal}
          initialData={
            (selectedEvent as TimeBlockEvent | null) ?? timeBlockSeed ?? null
          }
          isEdit={!!selectedEvent}
          isLoading={isLoading}
          onSubmit={handleTimeBlockSubmit}
          onDelete={handleTimeBlockDelete}
          timezone={timezone}
          viewedDay={viewedDay}
          events={events}
          nested={
            drawerSlot?.slot?.kind === "timeBlock"
              ? drawerSlot.slot.nested
              : null
          }
          onOpenNested={(n) => drawerSlot?.openNested(n)}
          onCloseNested={() => drawerSlot?.closeNested()}
          onDraftChange={onTimeBlockDraftChange}
        />
      )}
    </>
  );
};

export default SchedulingModals;
